
import { toast } from "sonner";
import type { AssistantCreateParams } from "openai/resources/beta/assistants";
import OpenAI from "openai";

export const uploadFileToOpenAI = async (openai: OpenAI, file: File) => {
  try {
    console.log('Uploading file to OpenAI:', file.name);
    const uploadedFile = await openai.files.create({
      file,
      purpose: 'assistants'
    });
    console.log('File uploaded to OpenAI:', uploadedFile);
    return uploadedFile;
  } catch (error) {
    console.error('Error uploading file to OpenAI:', error);
    throw error;
  }
};

export const createOpenAIVectorStore = async (openai: OpenAI, name: string) => {
  try {
    console.log('Creating vector store:', name);
    // Create the vector store with proper expiration policy using fetch API
    const response = await fetch('https://api.openai.com/v1/vector_stores', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${openai.apiKey}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      },
      body: JSON.stringify({
        name: `${name} Knowledge Base`,
        expires_after: {
          anchor: "last_active_at",
          days: 30
        }
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create vector store: ${errorText}`);
    }

    const vectorStore = await response.json();
    console.log('Vector store created:', vectorStore);
    return vectorStore;
  } catch (error) {
    console.error('Error creating vector store:', error);
    throw error;
  }
};

export const addFileToVectorStore = async (
  openai: OpenAI,
  vectorStoreId: string,
  fileId: string
) => {
  try {
    console.log('Adding file to vector store:', { vectorStoreId, fileId });
    
    const response = await fetch(`https://api.openai.com/v1/vector_stores/${vectorStoreId}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${openai.apiKey}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      },
      body: JSON.stringify({
        file_id: fileId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add file to vector store: ${errorText}`);
    }

    const file = await response.json();
    
    // Now poll for completion
    let complete = false;
    let attempts = 0;
    const maxAttempts = 15;
    
    while (!complete && attempts < maxAttempts) {
      attempts++;
      
      const statusResponse = await fetch(`https://api.openai.com/v1/vector_stores/${vectorStoreId}/files/${fileId}`, {
        headers: {
          'Authorization': `Bearer ${openai.apiKey}`,
          'OpenAI-Beta': 'assistants=v2'
        }
      });
      
      if (!statusResponse.ok) {
        throw new Error(`Failed to check file status: ${statusResponse.statusText}`);
      }
      
      const status = await statusResponse.json();
      console.log(`File status polling (attempt ${attempts}):`, status.status);
      
      if (status.status === 'completed') {
        complete = true;
      } else if (status.status === 'failed') {
        throw new Error(`File processing failed: ${status.last_error || 'Unknown error'}`);
      } else {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    console.log('File added to vector store and processed:', file);
    return file;
  } catch (error) {
    console.error('Error adding file to vector store:', error);
    throw error;
  }
};

export const enhanceInstructionsWithAI = async (
  openai: OpenAI,
  name: string,
  instructions: string
): Promise<string | undefined> => {
  try {
    console.log('Enhancing instructions for:', name);
    const completion = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [
        {
          role: "system",
          content: "You are an AI system prompt creator. Your role is to enhance the system prompt for a custom assistant based on the information provided by the user. You will be creative and use advance prompting techniques in the system prompt so the assistants can be better. Define characteristics, background, tone, style, knowledge, etc... Always encourage the use of the files in the database, to answer the email replying directly to the recipients name and for the assitant to remain in its role not matter what. You MUST only provide the system prompt in an organized answer."
        },
        {
          role: "user",
          content: `Assistant Name: ${name}\n\nOriginal Instructions: ${instructions}`
        }
      ],
    });

    const enhancedInstructions = completion.choices[0].message.content;
    console.log('Enhanced instructions:', enhancedInstructions);
    return enhancedInstructions;
  } catch (error) {
    console.error('Error enhancing instructions:', error);
    toast.error("Failed to enhance instructions");
    return undefined;
  }
};

export const pollForFileCompletion = async (
  openai: OpenAI,
  vectorStoreId: string,
  timeoutSeconds = 60
): Promise<boolean> => {
  const startTime = Date.now();
  const timeoutMs = timeoutSeconds * 1000;
  
  console.log(`Polling for file completion in vector store ${vectorStoreId}`);
  
  while (Date.now() - startTime < timeoutMs) {
    try {
      // Get vector store status using fetch API
      const response = await fetch(`https://api.openai.com/v1/vector_stores/${vectorStoreId}`, {
        headers: {
          'Authorization': `Bearer ${openai.apiKey}`,
          'OpenAI-Beta': 'assistants=v2'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch vector store status: ${response.statusText}`);
      }
      
      const vectorStore = await response.json();
      console.log('Vector store status:', vectorStore.status, 'File counts:', vectorStore.file_counts);
      
      // Check if all files are processed
      if (vectorStore.status === 'completed' || 
          (vectorStore.file_counts?.in_progress === 0 && 
           vectorStore.file_counts?.failed === 0 && 
           vectorStore.file_counts?.cancelled === 0 &&
           vectorStore.file_counts?.completed > 0)) {
        console.log('All files processed successfully');
        return true;
      }
      
      // If status is failed, throw error
      if (vectorStore.status === 'failed') {
        throw new Error('Vector store processing failed');
      }
      
      // Wait before polling again
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      console.error('Error polling for file completion:', error);
      throw error;
    }
  }
  
  console.warn('Polling timed out, vector store processing incomplete');
  return false;
};
